import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Plus, Edit, Trash2, Save, X, Layout } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface DashboardFeature {
  id?: string;
  title: string;
  description: string;
  path: string;
  icon_name?: string;
  color_scheme: string;
  category: string;
  requires_subscription: boolean;
  is_published: boolean;
  sort_order: number;
}

export default function DashboardFeaturesManager() {
  const [features, setFeatures] = useState<DashboardFeature[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingFeature, setEditingFeature] = useState<DashboardFeature | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  const defaultFeature: DashboardFeature = {
    title: '',
    description: '',
    path: '',
    color_scheme: 'primary',
    category: 'general',
    requires_subscription: false,
    is_published: true,
    sort_order: 0
  };

  useEffect(() => {
    loadFeatures();
  }, []);

  const loadFeatures = async () => {
    try {
      const { data, error } = await supabase
        .from('dashboard_features')
        .select('*')
        .order('sort_order', { ascending: true });
      
      if (error) throw error;
      setFeatures(data || []);
    } catch (error) {
      console.error('Error loading features:', error);
      toast.error('Failed to load dashboard features');
    } finally {
      setLoading(false);
    }
  };

  const saveFeature = async (feature: DashboardFeature) => {
    try {
      if (feature.id) {
        const { error } = await supabase
          .from('dashboard_features')
          .update(feature)
          .eq('id', feature.id);
        if (error) throw error;
        toast.success('Feature updated successfully');
      } else {
        const { error } = await supabase
          .from('dashboard_features')
          .insert([feature]);
        if (error) throw error;
        toast.success('Feature created successfully');
      }
      
      loadFeatures();
      setEditingFeature(null);
      setIsCreating(false);
    } catch (error) {
      console.error('Error saving feature:', error);
      toast.error('Failed to save feature');
    }
  };

  const deleteFeature = async (id: string) => {
    try {
      const { error } = await supabase
        .from('dashboard_features')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      toast.success('Feature deleted successfully');
      loadFeatures();
    } catch (error) {
      console.error('Error deleting feature:', error);
      toast.error('Failed to delete feature');
    }
  };

  const FeatureForm = ({ feature, onSave, onCancel }: { 
    feature: DashboardFeature; 
    onSave: (feature: DashboardFeature) => void; 
    onCancel: () => void; 
  }) => {
    const [formData, setFormData] = useState(feature);

    return (
      <Card className="mb-4">
        <CardHeader>
          <CardTitle>{feature.id ? 'Edit Dashboard Feature' : 'Create New Dashboard Feature'}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Daily Check-in"
              />
            </div>
            
            <div>
              <Label htmlFor="path">Path</Label>
              <Input
                id="path"
                value={formData.path}
                onChange={(e) => setFormData({ ...formData, path: e.target.value })}
                placeholder="/daily-check-in"
              />
            </div>
          </div>
          
          <div>
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Track your daily mood, energy, and goals"
              rows={3}
            />
          </div>
          
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label htmlFor="icon_name">Icon Name (Lucide)</Label>
              <Input
                id="icon_name"
                value={formData.icon_name || ''}
                onChange={(e) => setFormData({ ...formData, icon_name: e.target.value })}
                placeholder="CheckCircle"
              />
            </div>
            
            <div>
              <Label htmlFor="color_scheme">Color Scheme</Label>
              <Select value={formData.color_scheme} onValueChange={(value) => setFormData({ ...formData, color_scheme: value })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="primary">Primary</SelectItem>
                  <SelectItem value="secondary">Secondary</SelectItem>
                  <SelectItem value="accent">Accent</SelectItem>
                  <SelectItem value="muted">Muted</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label htmlFor="category">Category</Label>
              <Select value={formData.category} onValueChange={(value) => setFormData({ ...formData, category: value })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="general">General</SelectItem>
                  <SelectItem value="tracking">Tracking</SelectItem>
                  <SelectItem value="wellness">Wellness</SelectItem>
                  <SelectItem value="therapy">Therapy</SelectItem>
                  <SelectItem value="meditation">Meditation</SelectItem>
                  <SelectItem value="tools">Tools</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="sort_order">Sort Order</Label>
              <Input
                id="sort_order"
                type="number"
                value={formData.sort_order}
                onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })}
              />
            </div>
            
            <div className="space-y-2 pt-6">
              <div className="flex items-center space-x-2">
                <Switch
                  id="is_published"
                  checked={formData.is_published}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_published: checked })}
                />
                <Label htmlFor="is_published">Published</Label>
              </div>
              
              <div className="flex items-center space-x-2">
                <Switch
                  id="requires_subscription"
                  checked={formData.requires_subscription}
                  onCheckedChange={(checked) => setFormData({ ...formData, requires_subscription: checked })}
                />
                <Label htmlFor="requires_subscription">Premium Feature</Label>
              </div>
            </div>
          </div>
          
          <div className="flex gap-2">
            <Button onClick={() => onSave(formData)}>
              <Save className="h-4 w-4 mr-2" />
              Save
            </Button>
            <Button variant="outline" onClick={onCancel}>
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return <div>Loading dashboard features...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Dashboard Features Management</h2>
        <Button onClick={() => setIsCreating(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Add New Feature
        </Button>
      </div>
      
      <div className="p-4 bg-muted rounded-lg">
        <h3 className="font-semibold mb-2">🏠 Dashboard Control</h3>
        <p className="text-sm text-muted-foreground">
          Manage the feature tiles that appear on users' dashboard. Control visibility, organization, 
          premium requirements, and instantly add new features without app updates.
        </p>
      </div>
      
      {isCreating && (
        <FeatureForm
          feature={defaultFeature}
          onSave={saveFeature}
          onCancel={() => setIsCreating(false)}
        />
      )}
      
      {editingFeature && (
        <FeatureForm
          feature={editingFeature}
          onSave={saveFeature}
          onCancel={() => setEditingFeature(null)}
        />
      )}
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {features.map((feature) => (
          <Card key={feature.id} className={!feature.is_published ? 'opacity-60' : ''}>
            <CardHeader>
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Layout className="h-4 w-4" />
                    {feature.title}
                  </CardTitle>
                  <div className="flex gap-1 mt-1">
                    <Badge variant="secondary">{feature.category}</Badge>
                    <Badge variant="outline">{feature.color_scheme}</Badge>
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setEditingFeature(feature)}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => feature.id && deleteFeature(feature.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-3">{feature.description}</p>
              
              <div className="space-y-2">
                <div className="text-sm">
                  <strong>Path:</strong> <code className="text-xs">{feature.path}</code>
                </div>
                
                {feature.icon_name && (
                  <div className="text-sm">
                    <strong>Icon:</strong> {feature.icon_name}
                  </div>
                )}
                
                <div className="flex flex-wrap gap-1">
                  {feature.is_published ? (
                    <Badge variant="default">Published</Badge>
                  ) : (
                    <Badge variant="secondary">Draft</Badge>
                  )}
                  
                  {feature.requires_subscription && (
                    <Badge variant="outline">Premium</Badge>
                  )}
                </div>
                
                <div className="text-xs text-muted-foreground">
                  Sort Order: {feature.sort_order}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}